/**
 * Staff delivery of a Clinio Pay link from the payment window.
 * Email and SMS both carry the patient pay-invoice URL. Recording a
 * desk payment is a separate action and does not send this link.
 */

import { primaryTelephoneNumber } from '@/lib/patientStamkortFields';

export function patientAcceptsSms(consent: unknown): boolean {
  if (!consent || typeof consent !== 'object' || Array.isArray(consent)) return false;
  return (consent as { status?: unknown }).status === 'accepted';
}

export function contactFromSnapshot(snapshot: unknown): {
  email: string | null;
  phone: string | null;
} {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return { email: null, phone: null };
  }
  const row = snapshot as {
    email?: unknown;
    phone?: unknown;
    telephone_subscribers?: unknown;
  };
  const email = typeof row.email === 'string' && row.email.trim() ? row.email.trim() : null;
  return {
    email,
    phone: primaryTelephoneNumber(row.telephone_subscribers),
  };
}

export function hasPatientPhone(phone: string | null | undefined): boolean {
  return Boolean(phone && phone.trim().length >= 4);
}

const SMS_FAILURE_KEYS = {
  no_consent: 'payments.pay_link_sms_no_consent',
  sms_disabled: 'payments.pay_link_sms_disabled',
  insufficient_credits: 'payments.pay_link_sms_no_credits',
  quiet_hours: 'payments.pay_link_sms_quiet_hours',
  daily_cap_reached: 'payments.pay_link_sms_daily_cap',
  invalid_phone: 'payments.pay_link_sms_invalid_phone',
} as const;

export type PayLinkSmsFailure = keyof typeof SMS_FAILURE_KEYS;

export function payLinkSmsErrorKey(code: string | null | undefined): string | null {
  switch (code) {
    case 'no_consent':
    case 'sms_disabled':
    case 'insufficient_credits':
    case 'quiet_hours':
    case 'daily_cap_reached':
    case 'invalid_phone':
      return SMS_FAILURE_KEYS[code];
    default:
      return null;
  }
}

export function apiErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('details' in error)) return null;
  const details = (error as { details?: unknown }).details;
  if (!details || typeof details !== 'object' || !('error_code' in details)) return null;
  const code = (details as { error_code?: unknown }).error_code;
  return typeof code === 'string' ? code : null;
}
